import { MapPin, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { getCachedModelURL } from '@/lib/modelCache';
import { prettyModelName } from '@/lib/utils';
import type { Annotation } from '@/lib/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelPath: string;
  /** Shown in the dialog title — lets the same editor serve both "this model's defaults" and "override for this sequence" without knowing which itself. */
  scopeLabel: string;
  annotations: Annotation[];
  onSave: (annotations: Annotation[]) => void | Promise<void>;
}

type ModelViewerEl = HTMLElement & {
  positionAndNormalFromPoint?: (
    clientX: number,
    clientY: number,
  ) => { position: { toString(): string }; normal: { toString(): string } } | null;
};

function newId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `ann-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * model-viewer's Vector3D.toString() appends a unit suffix to every
 * component (e.g. "0.33m 1.3m 0.37m") — confirmed by actually placing a
 * pin and inspecting the saved JSON. Every other vec3 string in cAspeR
 * (Asset.position, WebContentOverlay.position, ...) is bare "x y z" for
 * A-Frame's parser, which chokes on a trailing unit letter. Strip it
 * here, once, at the only place model-viewer's own value ever enters
 * the app, rather than expecting every future consumer to know to.
 */
function stripUnits(vec3: string): string {
  return vec3
    .split(/\s+/)
    .map((n) => n.replace(/[a-z%]+$/i, ''))
    .join(' ');
}

/**
 * Click-to-place annotation editor over a live <model-viewer> — used both
 * to edit a model's own default annotations and a single sequence
 * asset's override set (the caller decides which by what it passes as
 * `annotations` and does with `onSave`; this component only ever edits
 * a plain list).
 *
 * "Add" is a deliberate armed mode (click the button, then click the
 * model) rather than every click placing a pin — matches the same
 * arm-then-touch pattern already used for the XR client's room-anchor
 * calibration, and avoids a stray orbit-drag on the model dropping an
 * accidental annotation.
 */
export function AnnotationEditorDialog({
  open,
  onOpenChange,
  modelPath,
  scopeLabel,
  annotations,
  onSave,
}: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const viewerRef = useRef<ModelViewerEl | null>(null);
  const [draft, setDraft] = useState<Annotation[]>(annotations);
  const [armed, setArmed] = useState(false);
  // Set only while repositioning an EXISTING annotation (as opposed to
  // placing a new one) — same armed-click flow, but the next model hit
  // updates that annotation's position/normal in place instead of
  // creating a new entry.
  const [repositioningId, setRepositioningId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingPoint, setPendingPoint] = useState<{ position: string; normal: string } | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset the whole draft to what was passed in each time the dialog is
  // (re)opened — editing is local until Save, same as SequenceEditor's
  // own overlay/scale drafts.
  useEffect(() => {
    if (open) {
      setDraft(annotations);
      setArmed(false);
      setRepositioningId(null);
      setSelectedId(null);
      setPendingPoint(null);
      setFormTitle('');
      setFormBody('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modelPath]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getCachedModelURL(modelPath)
      .then((u) => { if (!cancelled) setSrc(u); })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; };
  }, [open, modelPath]);

  const selected = draft.find((a) => a.id === selectedId) ?? null;

  const handleModelClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!armed) return;
    const el = viewerRef.current;
    if (!el?.positionAndNormalFromPoint) return;
    const hit = el.positionAndNormalFromPoint(e.clientX, e.clientY);
    if (!hit) return;
    const position = stripUnits(hit.position.toString());
    const normal = stripUnits(hit.normal.toString());

    if (repositioningId) {
      setDraft((d) => d.map((a) => (a.id === repositioningId ? { ...a, position, normal } : a)));
      setRepositioningId(null);
      setArmed(false);
      return; // keep the same annotation selected/open — only its point moved
    }

    setPendingPoint({ position, normal });
    setSelectedId(null);
    setFormTitle('');
    setFormBody('');
    setArmed(false);
  };

  const confirmPending = () => {
    if (!pendingPoint || !formTitle.trim()) return;
    const entry: Annotation = {
      id: newId(),
      position: pendingPoint.position,
      normal: pendingPoint.normal,
      title: formTitle.trim(),
      body: formBody.trim() || undefined,
    };
    setDraft((d) => [...d, entry]);
    setPendingPoint(null);
    setFormTitle('');
    setFormBody('');
  };

  const startEdit = (a: Annotation) => {
    setSelectedId(a.id);
    setPendingPoint(null);
    setRepositioningId(null);
    setArmed(false);
    setFormTitle(a.title);
    setFormBody(a.body ?? '');
  };

  const startReposition = (id: string) => {
    setRepositioningId(id);
    setArmed(true);
  };

  const confirmEdit = () => {
    if (!selected || !formTitle.trim()) return;
    setDraft((d) =>
      d.map((a) => (a.id === selected.id ? { ...a, title: formTitle.trim(), body: formBody.trim() || undefined } : a)),
    );
    setSelectedId(null);
    setFormTitle('');
    setFormBody('');
  };

  const removeAnnotation = (id: string) => {
    setDraft((d) => d.filter((a) => a.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
      setFormTitle('');
      setFormBody('');
    }
    if (repositioningId === id) {
      setRepositioningId(null);
      setArmed(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Annotations — {prettyModelName(modelPath)}</DialogTitle>
          <DialogDescription>{scopeLabel}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-[1fr_260px]">
          <div className="relative overflow-hidden rounded-md border bg-gradient-to-br from-cisco-navy/5 via-transparent to-primary/5">
            {src ? (
              <model-viewer
                key={src}
                ref={(el: HTMLElement | null) => {
                  viewerRef.current = el as ModelViewerEl | null;
                }}
                src={src}
                alt={prettyModelName(modelPath)}
                camera-controls
                shadow-intensity="1"
                exposure="1.0"
                loading="eager"
                onClick={handleModelClick}
                style={{
                  width: '100%',
                  height: '360px',
                  background: 'transparent',
                  cursor: armed ? 'crosshair' : 'grab',
                }}
              >
                {draft.map((a, i) => (
                  <button
                    key={a.id}
                    type="button"
                    slot={`hotspot-${a.id}`}
                    data-position={a.position}
                    data-normal={a.normal ?? '0 1 0'}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      startEdit(a);
                    }}
                    className={`grid h-6 w-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 text-[11px] font-bold shadow ${
                      a.id === selectedId
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-white bg-cisco-navy text-white'
                    }`}
                    title={a.title}
                  >
                    {i + 1}
                  </button>
                ))}
              </model-viewer>
            ) : (
              <div className="grid h-[360px] place-items-center text-sm text-muted-foreground">
                Loading model…
              </div>
            )}

            <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between p-2">
              <Button
                type="button"
                size="sm"
                variant={armed ? 'default' : 'outline'}
                className="pointer-events-auto"
                onClick={() => {
                  setArmed((v) => !v);
                  setRepositioningId(null);
                  setPendingPoint(null);
                  setSelectedId(null);
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {armed && repositioningId
                  ? 'Click the model to move it…'
                  : armed
                    ? 'Click the model to place…'
                    : 'Add annotation'}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {(pendingPoint || selected) ? (
              <div className="flex flex-col gap-2 rounded-md border p-3">
                <Label className="text-xs">Title</Label>
                <Input
                  autoFocus
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g. Power supply"
                />
                <Label className="text-xs">Description (optional)</Label>
                <textarea
                  value={formBody}
                  onChange={(e) => setFormBody(e.target.value)}
                  rows={3}
                  placeholder="Shown when this annotation is opened"
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={!formTitle.trim()}
                    onClick={pendingPoint ? confirmPending : confirmEdit}
                  >
                    {pendingPoint ? 'Add' : 'Update'}
                  </Button>
                  {selected && (
                    <Button
                      type="button"
                      size="sm"
                      variant={repositioningId === selected.id ? 'default' : 'outline'}
                      onClick={() => startReposition(selected.id)}
                      title="Click a new point on the model to move this annotation there"
                    >
                      <MapPin className="h-3.5 w-3.5" />
                      {repositioningId === selected.id ? 'Click the model…' : 'Reposition'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setPendingPoint(null);
                      setSelectedId(null);
                      setRepositioningId(null);
                      setArmed(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                Click <strong>Add annotation</strong>, then click a point on the model. Click an
                existing numbered pin to edit or remove it.
              </p>
            )}

            <div className="flex flex-1 flex-col gap-1 overflow-y-auto">
              {draft.length === 0 && (
                <p className="text-xs text-muted-foreground">No annotations yet.</p>
              )}
              {draft.map((a, i) => (
                <div
                  key={a.id}
                  className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${
                    a.id === selectedId ? 'border-primary bg-primary/5' : ''
                  }`}
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <button
                    type="button"
                    onClick={() => startEdit(a)}
                    className="min-w-0 flex-1 truncate text-left hover:underline"
                  >
                    {i + 1}. {a.title}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAnnotation(a.id)}
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${a.title}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
